import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { FilterBar } from "../../../components/PortfolioPerformance/filter-bar";
import { TrendLineChart, niceAxisMax } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import { WalTable, type WalNode } from "../../../components/PortfolioPerformance/wal-table";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { useWalOffices } from "../lib/api/weighted-average-life-reference";
import { useBorrowerIndustries } from "../lib/api/borrower-industries";
import {
  ALL_RATINGS_LABEL,
  matchRow,
  useWeightedAverageLife,
  useWeightedAverageLifeTrend,
  type WalTrendView,
} from "../lib/api/weighted-average-life";

// Real distinct lo_risk_rating values on wal_grouping (confirmed live) --
// same hardcoded list risk-pricing-engine's own WeightedAverageLife.tsx uses,
// since this is the frontend's own filter-options list, not a live-fetched
// reference endpoint, on either page.
const GRADE_OPTIONS = ["A", "B1", "B2", "B3", "Other"];

const ALL_OFFICES_LABEL = "All Offices";
const ALL_INDUSTRIES_LABEL = "All Industry";
const ALL_GRADES_LABEL = ALL_RATINGS_LABEL;

export default function LossDevelopmentWalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const office = searchParams.get("office") ?? undefined;
  const searchIndustry = searchParams.get("industry") ?? undefined;
  const searchGrade = searchParams.get("grade") ?? undefined;
  const grade = searchGrade && GRADE_OPTIONS.includes(searchGrade) ? searchGrade : undefined;

  const officesQuery = useWalOffices();
  const offices = officesQuery.data ?? [];
  const industriesQuery = useBorrowerIndustries();
  const industries = industriesQuery.data ?? [];
  const walQuery = useWeightedAverageLife();
  const rows = walQuery.data ?? [];

  // wal_grouping keys on office_number, not name -- Office Name is the only
  // filter here that needs resolving before it can match a row.
  const officeNumber = office
    ? offices.find((o) => o.officeName === office)?.officeNumber
    : undefined;

  // Annual by default; the toggle swaps the chart's own x-axis dates and WAL columns server-side.
  const [trendView, setTrendView] = useState<WalTrendView>("annual");
  const trendQuery = useWeightedAverageLifeTrend(officeNumber, searchIndustry, grade, trendView);
  const trendData = useMemo(
    () =>
      (trendQuery.data ?? []).map((point) => ({
        month: point.xAxis,
        realizedWal: point.realizedWal,
        activeWal: point.activeWal,
      })),
    [trendQuery.data],
  );

  // The y axis runs from 0 to a round number above the HIGHER of the two lines (whichever of Realized / Active is higher, at any point), so
  // neither is ever cut off or pushed against the top.
  const walAxisMax = useMemo(() => {
    const values = trendData.flatMap((p) => [p.realizedWal, p.activeWal]).filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    return values.length ? niceAxisMax(Math.max(...values) * 1.05) : undefined;
  }, [trendData]);

  function setFilterParam(key: "office" | "industry" | "grade", value: string, allLabel: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === allLabel) next.delete(key);
      else next.set(key, value);
      return next;
    });
  }

  // One top-level row per industry (or just the selected one), each resolved
  // against the current Office/Grade selection via the same sentinel match
  // the backend row itself uses. Sub-rows (one per real office) are only
  // built when no Office is selected -- narrowing the Office filter collapses
  // back to flat rows scoped to that one office.
  const walTree = useMemo<WalNode[]>(() => {
    const industryList = searchIndustry ? [searchIndustry] : industries;
    const nodes: WalNode[] = [];
    for (const industry of industryList) {
      const row = matchRow(rows, industry, officeNumber, grade);
      if (!row) continue;
      const subRows: WalNode[] = [];
      if (officeNumber == null) {
        for (const o of offices) {
          const officeRow = matchRow(rows, industry, o.officeNumber, grade);
          if (!officeRow) continue;
          subRows.push({
            id: `${industry}-${o.officeNumber}`,
            segment: o.officeName,
            segmentWal: officeRow.segmentWal,
            portfolioWal: officeRow.portfolioWal,
          });
        }
      }
      nodes.push({
        id: industry,
        segment: industry,
        segmentWal: row.segmentWal,
        portfolioWal: row.portfolioWal,
        subRows: subRows.length > 0 ? subRows : undefined,
      });
    }
    return nodes;
  }, [rows, industries, offices, searchIndustry, officeNumber, grade]);

  // portfolioWal is one constant across every row -- read off any row once
  // loaded, rather than a second request for a single number.
  const portfolioWal = rows[0]?.portfolioWal;

  return (
    <div>
      <PageHeader
        title="Weighted Average Life"
        subtitle={
          portfolioWal != null ? `Portfolio WAL: ${portfolioWal.toFixed(2)} years` : undefined
        }
      />

      <FilterBar
        className="mt-8 mb-5"
        filters={[
          {
            id: "office",
            label: "Office Name",
            value: office ?? ALL_OFFICES_LABEL,
            options: [ALL_OFFICES_LABEL, ...offices.map((o) => o.officeName)],
            onChange: (value) => setFilterParam("office", value, ALL_OFFICES_LABEL),
          },
          {
            id: "industry",
            label: "Industry",
            value: searchIndustry ?? ALL_INDUSTRIES_LABEL,
            options: [ALL_INDUSTRIES_LABEL, ...industries],
            onChange: (value) => setFilterParam("industry", value, ALL_INDUSTRIES_LABEL),
          },
          {
            id: "grade",
            label: "Grade",
            value: grade ?? ALL_GRADES_LABEL,
            options: [ALL_GRADES_LABEL, ...GRADE_OPTIONS],
            onChange: (value) => setFilterParam("grade", value, ALL_GRADES_LABEL),
          },
        ]}
      />

      <Panel
        title="Weighted Average Life Trend"
        description="Realized and active contracts, by year or over a trailing 12 months."
        right={
          <ToggleGroup
            type="single"
            value={trendView}
            onValueChange={(value) => value && setTrendView(value as WalTrendView)}
            className="border border-border bg-secondary"
            aria-label="Trend view"
          >
            <ToggleGroupItem value="annual" className="h-8 px-3 text-xs">
              Annual
            </ToggleGroupItem>
            <ToggleGroupItem value="trailing_12mo" className="h-8 px-3 text-xs">
              Trailing 12 Months
            </ToggleGroupItem>
          </ToggleGroup>
        }
      >
        {trendQuery.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load the weighted average life trend.
          </div>
        ) : trendQuery.isLoading || !trendQuery.data ? (
          <Skeleton className="h-[300px] w-full" />
        ) : (
          <TrendLineChart
            data={trendData}
            series={[
              { dataKey: "realizedWal", name: "Realized WAL", color: "var(--amber)" },
              { dataKey: "activeWal", name: "Active WAL", color: "hsl(var(--chart-2))" },
            ]}
            suffix=" yrs"
            {...(walAxisMax ? { yDomain: [0, walAxisMax] as [number, number] } : {})}
          />
        )}
      </Panel>

      <Panel
        className="mt-8"
        title="Weighted Average Life By Segment (Matured Contracts)"
        description="Segment WAL against the portfolio's overall WAL, by industry. Same source table as risk-pricing-engine's own Weighted Average Life page."
      >
        {walQuery.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load weighted average life.
          </div>
        ) : walQuery.isLoading || !walQuery.data ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <WalTable rows={walTree} />
        )}
      </Panel>
    </div>
  );
}
